import React, { useContext, useEffect } from "react";
import { Box, Divider, Grid, Typography, Skeleton } from "@mui/material";
import { PrimaryButton } from "../../../../components/ui/Button";
import {
  formatIndianRupees,
  formatNormalIndianRupees,
} from "../../../../utils/formatting";
import { useDispatch, useSelector } from "react-redux";
import { PaymentTermLoanContext } from "../../../../hooks/context";
import { authSelector } from "../../../../services/redux/slices/authSlice";
import Card from "../../../../components/ui/Card";
import { RadioInputField } from "../../../../components/ui/Inputs";
import { setStatusPopup } from "../../../../services/redux/slices/statusPopupSlice";
import { formatDate } from "../../../../utils/dateFormatUtils";
import { setPartPaymentType } from "../../../../services/redux/slices/documentFormSlice";
import AccountSelectorDropDown from "../../../../components/ui/AccountSelectorDropDown";
import ReactGA from "react-ga4";
import { decrypt } from "../../../../utils/crypto";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BottomModal from "../../../../components/ui/BottomModal";
import { paymentInfo } from "../../../../data/constant";
// import InfoLabelWithLink from "../../../../components/common/InfoLabelWithLink";

export interface RowData {
  [key: string]: string | number | null;
}

const PaymentRequestTermLoan = () => {
  const {
    setFormStep,
    partPaymentData,
    overDueValues,
    handleSelectPaymentType,
    selectedLoanNumber,
    LoanTypeValue,
    isLoading,
    rows,
    paymentType,
    formTypeValue,
    isXsOrSm,
    isOverDuePending,
  }: any = useContext(PaymentTermLoanContext);

  const { userProfile } = useSelector(authSelector);
  const [openDisbModal, setOpenDisbModal] = React.useState(false);
  // const partPay = partPaymentData && partPaymentData?.length ? partPaymentData[0]?.partPay : {};
  
  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo({
      top: 0,
      behavior: "smooth", // Adds smooth scrolling
    });
  }, []);

  const dispatch = useDispatch();
  const ALLOW_PARTPAYMENTS_TO_LOAN = ["ELFORN", "ELDOMC"];

  const handleShowMessage = (value: any) => {
    return `You already have an open ${value} request. Please complete all pending payments in it and ensure the request is either closed or expired before raising a new one. Note: Open requests auto expire within 12 hours if not closed.`;
  };

  const isPartPaymentExists: boolean = rows
    ? rows.some(
      (elem: any) => elem?.purpose == "PartPayment" && elem?.status == "Open"
    )
    : false;
  const isOverDueExists: boolean = rows
    ? rows.some(
      (elem: any) => elem?.purpose == "OverDue" && elem?.status == "Open"
    )
    : false;

  const isLanClose: boolean = ["M", "C", "W", "E", "S"].includes(
    partPaymentData?.[0]?.status
  );
  const htmlContent=()=>{
    return(
      paymentInfo?.map((item: any, index: any) => (
        <Grid item xs={12} key={index}>
          <Typography
            variant="body2"
            sx={{
              cursor: "pointer",
              color: "rgba(75, 70, 92, 1)",
              fontSize: "clamp(8px, 1vw, 12px)",
            }}
          >
            <li> {item?.label}</li>
            {/* <li> <InfoLabelWithLink label={item?.label} link={item?.link} /></li> */}
          </Typography>
        </Grid>
      ))
    )
  }
  return (
    <>
      {isLoading || isOverDuePending ? (
        <>
          <Grid item sm={12} xs={12} className="mt-20">
            <Card borderColor={!isXsOrSm}>
              {/* Main Heading and Subheading */}
              <Grid container sm>
                <Grid item xs={12} sm={12} sx={{ height: "28px" }}>
                  <Skeleton variant="text" width="60%" height={28} />
                  <Skeleton variant="text" width="40%" height={20} />
                </Grid>
              </Grid>

              {/* Divider */}
              <Grid container sx={{ paddingTop: isXsOrSm ? "12px" : "25px" }}>
                <Grid item sm={12}>
                  <Divider
                    sx={{
                      border: isXsOrSm
                        ? ""
                        : "1px solid rgba(219, 218, 222, 1)",
                      borderColor: isXsOrSm ? "rgba(0, 0, 0, 0.12)" : "",
                      borderBottomWidth: isXsOrSm ? "thin" : "",
                      width: "100%",
                    }}
                  />
                </Grid>
              </Grid>

              {/* Loan Details Section */}
              <Grid container sm>
                <Grid item xs={12} sm={6}>
                  <Grid
                    container
                    sm
                    className={
                      isXsOrSm
                        ? "dashboard-container-mobile"
                        : "dashboard-container"
                    }
                  >
                    {/* Loan Amount */}
                    <Grid item xs={6} sm={6} mt={2}>
                      <Skeleton variant="text" width="70%" height={20} />
                      <Skeleton variant="text" width="50%" height={20} />
                    </Grid>
                    {/* Overdue Amount */}
                    <Grid item xs={6} sm={6} mt={2}>
                      <Skeleton variant="text" width="70%" height={20} />
                      <Skeleton variant="text" width="50%" height={20} />
                    </Grid>
                    {/* Next Repay Date */}
                    <Grid item xs={6} sm={6}>
                      <Skeleton variant="text" width="70%" height={20} />
                      <Skeleton variant="text" width="50%" height={20} />
                    </Grid>
                    {/* Next Installment Amount */}
                    <Grid item xs={6} sm={6}>
                      <Skeleton variant="text" width="70%" height={20} />
                      <Skeleton variant="text" width="50%" height={20} />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Divider Line */}
                {!isXsOrSm && (
                  <Grid item sm={1} sx={{ paddingTop: "26px" }}>
                    <div className="line2-container">
                      <Skeleton
                        variant="rectangular"
                        width={4}
                        height="100%"
                        sx={{ margin: "auto" }}
                      />
                    </div>
                  </Grid>
                )}

                {/* Action Buttons Section */}
                <Grid
                  container
                  justifyContent={isXsOrSm ? "flex-start" : "center"}
                  alignItems={isXsOrSm ? "flex-start" : "center"}
                  sm={5}
                  xs={12}
                  sx={{
                    marginLeft: isXsOrSm ? "" : "-9px",
                    marginBottom: isXsOrSm ? "10px" : "0px",
                  }}
                  width="100%"
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems={isXsOrSm ? "flex-start" : "center"}
                    justifyContent={isXsOrSm ? "flex-start" : "center"}
                    gap={1}
                    width="100%"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent={isXsOrSm ? "flex-start" : "center"}
                      gap={2}
                      className={
                        isXsOrSm
                          ? "dashboard-container-mobile"
                          : "dashboard-container"
                      }
                      width="100%"
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="text" width={80} height={20} />
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="text" width={80} height={20} />
                      </Box>
                    </Box>
                    <Box>
                      <Skeleton
                        variant="rectangular"
                        width={isXsOrSm ? "100%" : 240}
                        height={40}
                        sx={{ borderRadius: "4px" }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </>
      ) : (
        <Grid item sm={12} className={isXsOrSm ? "" : "mt-20"}>
          {isXsOrSm && (
            <>
              <Box>
                <Typography
                  sx={{
                    fontWeight: "700",
                    fontSize: "14px",
                    letterSpacing: "1px",
                    color: "#168794",
                    paddingBottom: "15px",
                  }}
                >
                  Payment
                  {isXsOrSm && (
                    <span style={{ marginLeft: 6 }}>
                      <InfoOutlinedIcon sx={{ fontSize: 16, color: "#168794", verticalAlign: "middle" }} onClick={() => {setOpenDisbModal(true)}} />
                    </span>
                  )}
                </Typography>
              </Box>
            </>
          )}
          <Card borderColor={!isXsOrSm}>
            <Grid container sm>
              <Grid item sm={12} sx={{ height: "28px" }}>
                {isXsOrSm ? (
                  <>
                    <AccountSelectorDropDown
                      labelText="Loan account number"
                      buttonText=" N/A"
                      isActive={""}
                      backgroundColor="transparent"
                      borderWidth="0px"
                      marginTop="0px"
                    />
                  </>
                ) : (
                  <>
                    <Typography
                      variant="h6"
                      className="loan-account-main-heading"
                      style={{ marginBottom: "5px" }}
                    >
                      Loan A/c {selectedLoanNumber}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      className="loan-account-sub-heading"
                    >
                      {partPaymentData?.[0]?.description}
                    </Typography>
                  </>
                )}
              </Grid>
            </Grid>
            <Grid container sx={{ paddingTop: isXsOrSm ? "12px" : "25px" }}>
              {/* <Grid item sm={12}> */}
              <Divider
                sx={{
                  border: isXsOrSm ? "" : "1px solid rgba(219, 218, 222, 1)",
                  borderColor: isXsOrSm ? "rgba(0, 0, 0, 0.12)" : "",
                  borderBottomWidth: isXsOrSm ? "thin" : "",
                  width: "100%",
                }}
              />
              {/* </Grid> */}
            </Grid>
            <Grid
              container
              spacing={1}
              className={`${isXsOrSm ? "statement-container-mobile" : ""}`}
            >
              <Grid item xs={12} sm={6}>
                <Grid
                  container
                  spacing={2}
                  className={
                    isXsOrSm
                      ? "dashboard-container-mobile"
                      : "dashboard-container"
                  }
                >
                  <Grid item xs={6} sm={6} md={6} mt={2}>
                    <Typography
                      className={
                        isXsOrSm ? "insidePageLableMobile" : "insidePageLable"
                      }
                    >
                      Loan Amount
                    </Typography>
                    <Typography
                      className={
                        isXsOrSm ? "insidePageValueMobile" : "insidePageValue"
                      }
                      variant="subtitle1"
                    >
                      {formatIndianRupees(partPaymentData?.[0]?.loanAmount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6} mt={2}>
                    <Typography
                      className={
                        isXsOrSm ? "insidePageLableMobile" : "insidePageLable"
                      }
                    >
                      Overdue Amount
                    </Typography>
                    <Typography
                      className={
                        isXsOrSm ? "insidePageValueMobile" : "insidePageValue"
                      }
                    >
                      {formatNormalIndianRupees(overDueValues?.totalAmount)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={6}>
                    <Typography
                      className={
                        isXsOrSm ? "insidePageLableMobile" : "insidePageLable"
                      }
                    >
                      Next Repay Date
                    </Typography>
                    <Typography
                      className={
                        isXsOrSm ? "insidePageValueMobile" : "insidePageValue"
                      }
                    >
                      {formatDate(partPaymentData?.[0]?.nextEmiDate)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={6}>
                    <Typography
                      className={
                        isXsOrSm ? "insidePageLableMobile" : "insidePageLable"
                      }
                    >
                      Next installment amount
                    </Typography>
                    <Typography
                      className={
                        isXsOrSm ? "insidePageValueMobile" : "insidePageValue"
                      }
                    >
                      {formatIndianRupees(partPaymentData?.[0]?.emiAmount)}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              {!isXsOrSm && (
                <Grid item sm={1} sx={{ paddingTop: "31px !important" }}>
                  <div className="line2-container">
                    <div className="line vertical"></div>
                    <div className="line vertical"></div>
                  </div>
                </Grid>
              )}

              <Grid
                container
                justifyContent={isXsOrSm ? "flex-start" : "center"}
                alignItems={isXsOrSm ? "flex-start" : "center"}
                sm={5}
                xs={12}
                sx={{
                  marginLeft: isXsOrSm ? "" : "-9px",
                  marginBottom: isXsOrSm ? "10px" : "0px",
                }}
                width="100%"
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems={isXsOrSm ? "flex-start" : "center"}
                  justifyContent={isXsOrSm ? "flex-start" : "center"}
                  gap={1}
                  width="100%"
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={isXsOrSm ? "flex-start" : "center"}
                    gap={2}
                    className={
                      isXsOrSm
                        ? "dashboard-container-mobile"
                        : "dashboard-container"
                    }
                    width="100%"
                  >
                    {ALLOW_PARTPAYMENTS_TO_LOAN.includes(LoanTypeValue) && (
                      <Box display="flex" alignItems="center">
                        <RadioInputField
                          checked={paymentType == "1"}
                          onClick={() => {
                            // if(partPay?.isEnable === false)  {
                            //   dispatch(
                            //     setStatusPopup({
                            //       status: "error",
                            //       message: partPay?.message || "Part payment requests are disabled while your EMI is being processed.",
                            //     })
                            //   );
                            //   return;
                            // } 
                            if (isOverDuePending) {
                              dispatch(
                                setStatusPopup({
                                  status: "error",
                                  message:
                                    "We're experiencing technical issues, so your data is temporarily unavailable. We're working to fix this as quickly as possible. Thank you for your patience",
                                })
                              );
                            } else {
                              if (overDueValues?.totalAmount > 10) {
                                dispatch(
                                  setStatusPopup({
                                    status: "error",
                                    message:
                                      "Please clear the overdue before proceeding for part payment",
                                  })
                                );
                              } else if (overDueValues?.totalAmount >= 1 && overDueValues?.totalAmount <= 10) {
                                handleSelectPaymentType("1");
                                dispatch(
                                  setStatusPopup({
                                    status: "info",
                                    message: `Please note: The existing overdue of ₹${overDueValues?.totalAmount} will be automatically adjusted when you make this part payment`,
                                  })
                                );
                              }
                              else if (isPartPaymentExists) {
                                dispatch(
                                  setStatusPopup({
                                    status: "error",
                                    message: handleShowMessage("part payment"),
                                  })
                                );
                              } 
                              // else if (
                              //   partPaymentData?.[0]?.elegibleForPartpayment
                              // ) {
                              //   dispatch(
                              //     setStatusPopup({
                              //       status: "error",
                              //       message:
                              //         "Partpayment is not allowed within six months of the last disbursement.",
                              //     })
                              //   );
                              // } 
                              else if (rows?.[0]?.statusInfoForPartPayment) {
                                dispatch(
                                  setStatusPopup({
                                    status: "error",
                                    message:
                                      "You have a part payment request with its Amortization updation in progress. Please wait till it is updated before raising a new request. Note: You can track the status of your previous requests using “View Status” from previous payment details table",
                                  })
                                );
                              } else {
                                localStorage.setItem("payType", "PartPayment")
                                handleSelectPaymentType("1");
                              }
                            }
                          }}
                        />
                        <Typography className="paymentRadioItem">
                          Part payment
                        </Typography>
                      </Box>
                    )}
                    <Box display="flex" alignItems="center">
                      <RadioInputField
                        checked={paymentType == "2"}
                        onClick={() => {
                          if (
                            overDueValues?.totalAmount <= 0 ||
                            overDueValues == null
                          ) {
                            dispatch(
                              setStatusPopup({
                                status: "error",
                                message:
                                  "There is no overdue for the selected loan account",
                              })
                            );
                          } else if (isOverDueExists) {
                            dispatch(
                              setStatusPopup({
                                status: "error",
                                message: handleShowMessage("overdue"),
                              })
                            );
                          } else {
                            localStorage.setItem("payType", "OverDue")
                            handleSelectPaymentType("2");
                          }
                        }}
                      />
                      <Typography className="paymentRadioItem">
                        Overdue
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems={isXsOrSm ? "flex-start" : "center"}
                    justifyContent={isXsOrSm ? "flex-start" : "center"}
                    gap={1}
                    width="100%"
                    sx={{ marginLeft: isXsOrSm ? "5px" : "0px" }}
                  >
                    <PrimaryButton
                      disabled={!paymentType}
                      width={isXsOrSm ? "100%" : "240px"}
                      onClick={() => {
                        ReactGA.event({
                          category:
                            paymentType == "1"
                              ? "Dash_Pay_PartPay_Paynow_1"
                              : "Dash_Pay_Overdue_Paynow_1",
                          action:
                            paymentType == "1"
                              ? "Dash_Pay_PartPay_Paynow_1"
                              : "Dash_Pay_Overdue_Paynow_1",
                          label:
                            paymentType == "1"
                              ? "Dash_Pay_PartPay_Paynow_1"
                              : "Dash_Pay_Overdue_Paynow_1",
                          value: Number(decrypt(userProfile?.id)),
                        });

                        if (isLanClose) {
                          dispatch(
                            setStatusPopup({
                              status: "no access",
                              message:
                                "Payment requests are currently unavailable for this loan",
                            })
                          );
                        } else {
                          setFormStep(0);
                          dispatch(setPartPaymentType(paymentType));
                        }
                      }}
                    >
                      Pay Now
                    </PrimaryButton>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      )}
      <BottomModal 
        open={openDisbModal}
        onClose={() => {setOpenDisbModal(false)}}
        children={htmlContent()}
        title="Information related to Payment"
        showCloseButton = {true}
        closeOnBackdropClick = {false}
      />
    </>
  );
};

export default PaymentRequestTermLoan;
